import { Plus } from '@phosphor-icons/react';
import { CellValueChangedEvent, RowClassParams } from 'ag-grid-community';
import { uniqueId } from 'lodash';
import { useCallback, useMemo } from 'react';
import { FieldErrors, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { IBomPart } from 'bom/types';
import { AgGridTable, Button } from 'core/components';
import {
  AgGridClientTableProvider,
  useAgGridClientTableContext,
  useFormContext,
} from 'core/states';

import { getColumnDefs } from './columns';

interface IProps {
  bomPart: IBomPart;
}

export const Table = ({ bomPart }: IProps) => {
  const { control, errors } = useFormContext();

  const { fields, update } = useFieldArray({
    control,
    name: 'elements',
  });

  const columnDefs = useMemo(
    () => getColumnDefs((errors as FieldErrors).elements, bomPart),
    [errors, bomPart]
  );

  const defaultColDef = useMemo(
    () => ({
      wrapHeaderText: true,
      autoHeaderHeight: true,
      filter: false,
      floatingFilter: false,
      sortable: false,
      resizable: false,
      flex: 1,
      enableRowGroup: false,
      suppressHeaderMenuButton: true,
    }),
    []
  );

  const onCellValueChanged = useCallback(
    (_, { colDef: { field }, data, newValue, rowIndex }: CellValueChangedEvent) => {
      data[field] = newValue;

      update(rowIndex, data);

      return { updatedRow: data, options: { redrawRows: false } };
    },
    [update]
  );

  const getRowClass = useCallback(({ data }: RowClassParams) => {
    if (data?.deleted) {
      return 'deletedRow disabledRow';
    }

    return '';
  }, []);

  return (
    <AgGridClientTableProvider
      tableId="elementTable"
      entityType="Element"
      defaultColDef={defaultColDef}
      columnDefs={columnDefs}
      data={fields}
      isDataLoading={false}
      loadingPercentage={100}
      dataCurrentPage={1}
      onCellValueChanged={onCellValueChanged}
      getRowClass={getRowClass}
      rowHeight={60}
      sideBar={{}}
      hasTopActions={false}
      hasRowSelection={false}
    >
      <TableWithinProvider />
    </AgGridClientTableProvider>
  );
};

const TableWithinProvider = () => {
  const { t } = useTranslation();

  const { control } = useFormContext();
  const { updateRows } = useAgGridClientTableContext();

  const { append } = useFieldArray({
    control,
    name: 'elements',
  });

  const onAddRow = () => {
    const newRow = { id: uniqueId(), element: null, quantity: null, deleted: false, isNew: true };

    append(newRow);
    updateRows({
      add: [newRow],
    });
  };

  return (
    <>
      <AgGridTable />

      <Button
        text={t('buttonLabelAddElement')}
        onClick={onAddRow}
        startDecorator={<Plus />}
        sx={{ width: 'fit-content' }}
      />
    </>
  );
};
